import { Injectable, Injector } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  skipWhile,
  switchMap,
} from 'rxjs';
import { captureException } from '@sentry/angular';
import { AnalyticsEventType } from '../models/analytics';
import { EmbedEventMessage, EmbedEventName } from '../models/embed-analytics-events';
import {
  ActionsQueryParamKeys,
  checkIfQueryParamSetToTrue,
} from '../common/utils/actions-query-params-util';
import { SystemItemState } from '../models/system-status';
import { DeviceErrorType } from '../models/device-manger';
import { SpaceRepository } from '../state/space.repository';
import { DeviceType } from '../common/utils/devices-handle-util';
import { MediaDeviceState } from '../utilities/webrtc-stats-reporter/webrtc-stats-reporter-types';
import { CallAndPresencesService } from '../sessions/session/wb-top-controls-v2/services/call-and-presences.service';
import { PanelView } from '../sessions/panel/panel.component';
import { FLAGS, FlagsService } from './flags.service';
import { SessionSharedDataService } from './session-shared-data.service';
import { PresenceProvider } from './presence-provider';
import { UserService } from './user.service';
import { SpaceRecordingService } from './space-recording.service';
import { ProviderStateService } from './provider-state.service';
import { DevicesManagerService } from './devices-manager.service';

enum EmittedNetworkAndDeviceState {
  Stable = 'Stable',
  Unstable = 'Unstable',
}

interface UserInfo {
  id: string;
  name: string;
  profileImage: string;
  fullName: string;
}

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class AnalyticsEventsWindowEmitter {
  private ambientInitialized = false;
  // Last-emitted values to avoid duplicates
  private lastMicStatus?: DeviceErrorType;
  private lastCamStatus?: DeviceErrorType;
  private lastConnectionStatus?: EmittedNetworkAndDeviceState;
  private lastDevicePerformanceStatus?: EmittedNetworkAndDeviceState;

  constructor(private flagsService: FlagsService, private injector: Injector) {}

  // ---- Ambient emitters wiring ----
  public initAmbientEmitters(): void {
    if (this.ambientInitialized || !this.isEmittingEventsToWindowEnabled()) {
      return;
    }
    this.initReadyEmitter();
    this.initRecordingEmitter();
    this.initChatToggleEmitter();
    this.initScreenshareToggleEmitter();
    this.initPresenceEmitters();
    this.ambientInitialized = true;
  }

  private initReadyEmitter(): void {
    // ready
    const spaceRepo = this.injector.get(SpaceRepository);
    spaceRepo.hasActiveSpaceSynced$
      .pipe(
        untilDestroyed(this),
        filter((ready) => ready),
        distinctUntilChanged(),
      )
      .subscribe(() => this.emitReady());
  }

  private initRecordingEmitter(): void {
    // recording_status_change (start emitting on first true)
    const spaceRecordingService = this.injector.get(SpaceRecordingService);
    spaceRecordingService.isAnyoneRecording$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        skipWhile((isRec) => isRec === false),
      )
      .subscribe((isRec) => this.emitRecordingStatus(isRec));
  }

  private initChatToggleEmitter(): void {
    // chat toggle from left panel
    const sessionSharedDataService = this.injector.get(SessionSharedDataService);
    sessionSharedDataService.leftPanelView
      .pipe(
        map((state) => Boolean(state?.panelView === PanelView.chat)),
        distinctUntilChanged(),
        skipWhile((open) => open === false),
        untilDestroyed(this),
      )
      .subscribe((open) => this.emitChatToggle(open));
  }

  private initScreenshareToggleEmitter(): void {
    // screenshare toggle
    const devicesManagerService = this.injector.get(DevicesManagerService);
    devicesManagerService.isSharingScreen
      .pipe(
        map((mode) => Boolean(mode)),
        distinctUntilChanged(),
        skipWhile((on) => on === false),
        untilDestroyed(this),
      )
      .subscribe((enabled) => this.emitScreenshareToggle(enabled));
  }

  private initPresenceEmitters(): void {
    // presence -> participant events + local join/leave
    const spaceRepo = this.injector.get(SpaceRepository);
    const presenceProvider = this.injector.get(PresenceProvider);
    const userService = this.injector.get(UserService);
    const callAndPresenceService = this.injector.get(CallAndPresencesService);
    spaceRepo.spaceIdAndRoomId$
      .pipe(
        untilDestroyed(this),
        switchMap(([spaceId, roomId]) =>
          combineLatest([
            presenceProvider.getCallPresenceActivity(spaceId as string, roomId as string),
            userService.user.asObservable(),
          ]),
        ),
        pairwise(),
      )
      .subscribe((value) => {
        const [[prevSet], [currSet]] = value as [[Set<string>, any], [Set<string>, any]];

        if (prevSet.size !== currSet.size) {
          this.emitParticipantUpdate(currSet.size);
        }

        const localUserId = userService.userId;
        if (localUserId) {
          const wasIn = prevSet.has(localUserId);
          const isIn = currSet.has(localUserId);
          if (!wasIn && isIn) {
            this.emit(AnalyticsEventType.JOIN_CALL);
          } else if (wasIn && !isIn) {
            this.emit(AnalyticsEventType.LEAVE_CALL);
          }
        }

        const joined = [...currSet].filter((id) => !prevSet.has(id));
        const left = [...prevSet].filter((id) => !currSet.has(id));

        const userLookup = callAndPresenceService.usersLookup;
        const userIdToInfo = (userId) => {
          const user = userLookup[userId];
          return {
            id: userId,
            name: user.name,
            profileImage: user.profile_image,
            // using as any here since fulLName does not exists on the type of the model we use for the user
            fullName: (user as any)?.fullName,
          };
        };

        joined.map(userIdToInfo).forEach((userInfo) => this.emitParticipantJoin(userInfo));
        left.map(userIdToInfo).forEach((userInfo) => this.emitParticipantLeave(userInfo));
      });
  }

  // ---- Public API: AnalyticsEventType mapping ----
  emit(eventType: AnalyticsEventType): void {
    const message = this.buildMessage(eventType);
    if (!message) {
      return;
    }
    this.emitEvent(message.type, message.detail);
  }

  private buildMessage(eventType: AnalyticsEventType): EmbedEventMessage | undefined {
    switch (eventType) {
      case AnalyticsEventType.UNMUTE_VIDEO:
        return { type: EmbedEventName.CAMERA_TOGGLE, detail: { enabled: true } };
      case AnalyticsEventType.MUTE_VIDEO:
        return { type: EmbedEventName.CAMERA_TOGGLE, detail: { enabled: false } };
      case AnalyticsEventType.UNMUTE_AUDIO:
        return { type: EmbedEventName.MICROPHONE_TOGGLE, detail: { enabled: true } };
      case AnalyticsEventType.MUTE_AUDIO:
        return { type: EmbedEventName.MICROPHONE_TOGGLE, detail: { enabled: false } };
      case AnalyticsEventType.HAND_RAISED:
        return { type: EmbedEventName.HAND_RAISED, detail: {} };
      case AnalyticsEventType.HIDE_TAB:
        return { type: EmbedEventName.PAGE_VISIBILITY_CHANGE, detail: { visible: false } };
      case AnalyticsEventType.UNHIDE_TAB:
        return { type: EmbedEventName.PAGE_VISIBILITY_CHANGE, detail: { visible: true } };
      case AnalyticsEventType.JOIN_CALL:
        return { type: EmbedEventName.USER_JOINED_CALL, detail: {} };
      case AnalyticsEventType.LEAVE_CALL:
        return { type: EmbedEventName.USER_LEFT_CALL, detail: {} };
      default:
        return undefined;
    }
  }

  // ---- Convenience emits (non-AnalyticsEventType) ----
  emitReady(): void {
    this.emitEvent(EmbedEventName.READY, {});
  }

  emitChatToggle(open: boolean): void {
    this.emitEvent(EmbedEventName.CHAT_TOGGLE, { open });
  }

  emitPiPToggle(open: boolean): void {
    this.emitEvent(EmbedEventName.PIP_TOGGLE, { open });
  }

  emitScreenshareToggle(enabled: boolean): void {
    this.emitEvent(EmbedEventName.SCREENSHARE_TOGGLE, { enabled });
  }

  emitRecordingStatus(recordingOn: boolean): void {
    this.emitEvent(EmbedEventName.RECORDING_STATUS_CHANGE, {
      status: recordingOn ? 'started' : 'stopped',
    });
  }

  emitConnectionStatus(status: EmittedNetworkAndDeviceState): void {
    this.emitEvent(EmbedEventName.CONNECTION_STATUS_CHANGE, { status });
  }

  emitDevicePerformanceStatus(status: EmittedNetworkAndDeviceState): void {
    this.emitEvent(EmbedEventName.DEVICE_PERFORMANCE_STATUS_CHANGE, { status });
  }

  // Accepts a snapshot of current system status and emits only if values changed
  processSystemStatusSnapshot(
    micState: MediaDeviceState,
    camState: MediaDeviceState,
    isNetworkStable: boolean,
    isDeviceStruggling: boolean,
  ): void {
    // Network state
    const nextConnectionStatus = isNetworkStable
      ? EmittedNetworkAndDeviceState.Stable
      : EmittedNetworkAndDeviceState.Unstable;
    if (nextConnectionStatus !== this.lastConnectionStatus) {
      this.emitConnectionStatus(nextConnectionStatus);
      this.lastConnectionStatus = nextConnectionStatus;
    }

    // Device perf state
    const nextDevicePerfStatus = isDeviceStruggling
      ? EmittedNetworkAndDeviceState.Unstable
      : EmittedNetworkAndDeviceState.Stable;
    if (nextDevicePerfStatus !== this.lastDevicePerformanceStatus) {
      this.emitDevicePerformanceStatus(nextDevicePerfStatus);
      this.lastDevicePerformanceStatus = nextDevicePerfStatus;
    }

    // Cam and mic state
    const nextMicStatus = micState?.errorType;
    const nextCamStatus = camState?.errorType;

    const providerState = this.injector.get(ProviderStateService);
    const isCallConnected = providerState?.callConnectedValue ?? false;
    if (isCallConnected) {
      if (nextMicStatus !== this.lastMicStatus) {
        this.emitDeviceStatus(DeviceType.AUDIO, nextMicStatus);
        this.lastMicStatus = nextMicStatus;
      }

      if (nextCamStatus !== this.lastCamStatus) {
        this.emitDeviceStatus(DeviceType.VIDEO, nextCamStatus);
        this.lastCamStatus = nextCamStatus;
      }
    }
  }

  emitUserEndedCall(): void {
    this.emitEvent(EmbedEventName.USER_ENDED_CALL_FOR_EVERYONE, {});
  }

  emitParticipantUpdate(count: number): void {
    this.emitEvent(EmbedEventName.PARTICIPANT_UPDATE, { count });
  }

  emitParticipantJoin(userInfo: UserInfo): void {
    this.emitEvent(EmbedEventName.PARTICIPANT_JOIN, userInfo);
  }

  emitParticipantLeave(userInfo: UserInfo): void {
    this.emitEvent(EmbedEventName.PARTICIPANT_LEAVE, userInfo);
  }

  // Unified device status emission
  emitDeviceStatus(
    deviceType: DeviceType.AUDIO | DeviceType.VIDEO,
    deviceErrorType?: DeviceErrorType,
  ): void {
    this.emitEvent(EmbedEventName.DEVICE_STATUS_CHANGE, {
      deviceType: deviceType === DeviceType.AUDIO ? 'microphone' : 'camera',
      state: !deviceErrorType ? SystemItemState.GOOD : deviceErrorType,
    });
  }

  // ---- Core emit path ----
  private emitEvent(type: EmbedEventName, detail: any): void {
    if (!this.isEmittingEventsToWindowEnabled()) {
      return;
    }
    try {
      window.parent.postMessage({ type, detail }, '*');
    } catch (e) {
      captureException(e);
    }
  }

  private isEmittingEventsToWindowEnabled(): boolean {
    try {
      return (
        checkIfQueryParamSetToTrue(ActionsQueryParamKeys.BROADCAST_EVENTS) &&
        this.flagsService.isFlagEnabled(FLAGS.EMIT_EMBED_ANALYTICS)
      );
    } catch {
      return false;
    }
  }
}
